import React, { useState, useEffect, useCallback, useRef, Component } from 'react';
import { T, FONTS } from './constants/theme.js';
import {
  fetchProperties, getToken, getTenantSlug, setPropertyId, clearAuth,
  fetchTenantModules, checkTenantSubscription,
  receiveAuthFromParent, silentRefreshToken, refreshTenantSchema,
  getCachedProperties, getCurrentUser, isTokenExpiringSoon,
} from './services/api.js';
import { Spinner } from './components/ui.jsx';

// ── Bootstrap loading screen ──────────────────────────────────────────────────
// Shown AFTER login while SQLite is being populated for the first time.
// Disappears when the first full sync cycle completes (phase idle/done/complete)
// OR after 30 seconds so a slow server never permanently blocks the user.
function BootstrapLoadingScreen({ progress }) {
  const pct = Math.max(5, Math.min(100, progress || 5));
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(135deg,${T.navyDeep},${T.navyMid})`,
      fontFamily: FONTS.body, gap: 24, padding: 32,
    }}>
      <div style={{ fontSize: 48 }}>🏨</div>
      <div style={{ textAlign: 'center', maxWidth: 440 }}>
        <h2 style={{ color: T.white, fontFamily: FONTS.display, fontSize: 22, marginBottom: 8 }}>
          Loading Hotel Data
        </h2>
        <p style={{ color: '#7A9CC0', fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>
          Downloading all reservations, guests, and room data into your local
          database so everything works offline. This only happens once.
        </p>
        <div style={{ height: 8, background: '#1a3050', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
          <div style={{
            height: '100%', borderRadius: 4,
            background: `linear-gradient(90deg,${T.teal},${T.gold})`,
            width: `${pct}%`,
            transition: 'width .5s ease',
            boxShadow: `0 0 10px ${T.teal}88`,
          }} />
        </div>
        <p style={{ color: '#4A6080', fontSize: 12, fontFamily: 'monospace' }}>{pct}%</p>
      </div>
      <Spinner size={24} />
    </div>
  );
}

// autoCacheEngine is removed in the desktop build — all warming is handled
// by the SyncWorker in Electron main. These are permanent no-ops.
function registerAutoCacheProperty(_property) {}
function warmCacheForView(_view, _entityId) {}

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }

  static getDerivedStateFromError(error) { return { hasError: true, error }; }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught render error:', error, info?.componentStack?.slice(0, 400));
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const err = this.state.error;
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `linear-gradient(135deg,${T.navyDeep},${T.navyMid})`, fontFamily: FONTS.body, padding: 20 }}>
        <div style={{ background: T.white, borderRadius: 18, padding: '40px 36px', maxWidth: 520, width: '100%',
          textAlign: 'center', boxShadow: '0 16px 48px rgba(0,0,0,.2)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontFamily: FONTS.display, fontSize: 22, color: T.navy, marginBottom: 8 }}>
            Something went wrong
          </h2>
          <p style={{ fontSize: 13, color: T.muted, marginBottom: 8, lineHeight: 1.6 }}>
            {err?.message || 'An unexpected error occurred in the application.'}
          </p>
          <p style={{ fontSize: 11, color: T.muted, marginBottom: 24, background: `${T.gold}18`,
            borderRadius: 8, padding: '10px 16px', lineHeight: 1.5 }}>
            Your offline data is safe. Reload the page to recover.
          </p>
          <button onClick={() => window.location.reload()}
            style={{ background: T.teal, border: 'none', borderRadius: 10, padding: '12px 28px',
              fontSize: 13, fontWeight: 700, color: T.white, cursor: 'pointer', fontFamily: FONTS.body }}>
            🔄 Reload App
          </button>
          <button onClick={() => this.setState({ hasError: false, error: null })}
            style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 20px',
              fontSize: 12, color: T.muted, cursor: 'pointer', fontFamily: FONTS.body, marginLeft: 10 }}>
            Try Again
          </button>
        </div>
      </div>
    );
  }
}

import StandaloneLogin   from './views/auth/StandaloneLogin.jsx';
import FrontDeskLayout   from './layouts/FrontDeskLayout.jsx';
import Dashboard         from './views/dashboard/Dashboard.jsx';
import ReservationList   from './views/reservations/ReservationList.jsx';
import CreateReservation from './views/reservations/CreateReservation.jsx';
import ReservationDetail from './views/reservations/ReservationDetail.jsx';
import RoomStatusBoard   from './views/rooms/RoomStatusBoard.jsx';
import RatePlans         from './views/rates/RatePlans.jsx';
import FolioSheet        from './views/folio/FolioSheet.jsx';
import HousekeepingBoard from './views/housekeeping/HousekeepingBoard.jsx';
import Reports           from './views/reports/Reports.jsx';
import DistrictReport    from './views/reports/DistrictReport.jsx';
import PropertySetup     from './views/settings/PropertySetup.jsx';
import GuestManagement   from './views/guests/GuestManagement.jsx';
import CorporateAccounts from './views/corporate/CorporateAccounts.jsx';
import CorporateSessions from './views/corporate/CorporateSessions.jsx';
import StaffManagement   from './views/staff/StaffManagement.jsx';
import StaffDetail       from './views/staff/StaffDetail.jsx';
import AuditLogs         from './views/audit/AuditLogs.jsx';
import OnlineBooking     from './views/booking/OnlineBooking.jsx';
import FrontDesk         from './views/frontdesk/FrontDesk.jsx';
import ProfilePage       from './views/profile/ProfilePage.jsx';
import EmailConfig         from './views/settings/EmailConfig.jsx';
import FinancialStatement  from './views/financial/FinancialStatement.jsx';
import GuestBookingPage    from './views/booking/GuestBookingPage.jsx';

/**
 * Determine whether the app needs a login screen.
 *
 * Authenticated if ANY of:
 *  - Running inside an iframe  → Main Hall will post HOTELMIS_AUTH shortly
 *  - A token is already stored → previous staff login still valid
 *  - URL has ?token=           → legacy deep-link (api.js strips it immediately)
 *  - URL has ?tenant= only     → standalone staff portal; show login form
 */
function needsLogin() {
  const p         = new URLSearchParams(window.location.search);
  const hasToken  = !!p.get('token') || !!getToken();
  const isIframe  = window !== window.parent;
  return !hasToken && !isIframe;
}

export default function App() {
  // ── Public booking widget — no auth, accessed via ?book=1&tenant=slug ─
  const _p = new URLSearchParams(window.location.search);
  if (_p.get('book') === '1' || window.location.pathname.startsWith('/book')) {
    return <GuestBookingPage />;
  }
  const [view,        setView]       = useState('dashboard');
  const [selectedId,  setSelId]      = useState(null);
  const [property,    setProperty]   = useState(null);
  const [loading,     setLoading]    = useState(true);
  const [error,       setError]      = useState(null);
  // setupWarning: set when the server was unreachable on first load but we're
  // showing PropertySetup anyway (first-time TENANT_ADMIN, no cached property).
  // Cleared once the tenant successfully saves their property.
  const [setupWarning, setSetupWarning] = useState(null);
  const [offlineBoot, setOfflineBoot] = useState(false);
  const [authed,      setAuthed]     = useState(!needsLogin());
  const [modules,     setModules]    = useState([]);
  const [tenantBlock, setTenantBlock] = useState(null);

  // When true, we're inside an iframe waiting for HOTELMIS_AUTH from parent.
  // We don't try to load data until the token actually arrives.
  const awaitingParentAuth = useRef(window !== window.parent && !getToken());

  // ── Desktop bootstrap state ──────────────────────────────────────────────
  // Shows after login while SQLite is being populated for the first time.
  const [bootstrapNeeded,   setBootstrapNeeded]   = useState(false);
  const [bootstrapProgress, setBootstrapProgress] = useState(5);

  const fetchingRef = useRef(false);
  const fetchedRef  = useRef(false);
  const tenantSlug  = getTenantSlug();

  // ── Tenant subscription check ─────────────────────────────────────────
  const checkTenant = useCallback(async (slug) => {
    if (!slug || slug === 'dev') return;
    try {
      const res  = await checkTenantSubscription(slug);
      const data = res?.data ?? res;
      if (data?.valid === false) {
        setTenantBlock({
          reason:      data.reason      || 'No active reservation module subscription',
          redirectUrl: data.redirectUrl || 'http://localhost:3000/modules',
        });
      }
    } catch (_) { /* fail-open */ }
  }, []);

  // ── Load property — cache-first for instant boot, then refresh from network ─
  const loadProperty = useCallback(async () => {
    if (fetchingRef.current || fetchedRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    let bootedFromCache = false;

    // 1. Show UI immediately from cache (auto-warmer starts in parallel)
    try {
      const cached = await getCachedProperties();
      if (cached.length > 0) {
        setProperty(cached[0]);
        setPropertyId(cached[0].id);
        registerAutoCacheProperty(cached[0]);
        setOfflineBoot(true);
        bootedFromCache = true;
        fetchedRef.current = true;
        setLoading(false);
      }
    } catch { /* no cache yet */ }

    // 2. Refresh from network only when the browser is online
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      fetchingRef.current = false;
      if (!bootedFromCache) setLoading(false); // prevent permanent spinner when offline with no cache
      return;
    }

    try {
      // FIX (critical): refreshTenantSchema() must NOT run concurrently with fetchProperties().
      //
      // The previous code called both at the same time:
      //   refreshTenantSchema().catch(() => {});   ← POST /module/refresh-schema
      //   const data = await fetchProperties();    ← GET  /api/v1/properties
      //
      // On the backend, refreshSchema calls schemaBootstrapper.evict(tenant) +
      // schemaBootstrapper.ensureSchema(tenant) — the latter holds a `synchronized`
      // lock while re-running ALL Liquibase migrations (can take 15-30 s on a cold DB).
      // fetchProperties() hits JwtAuthFilter → also calls ensureSchema() → waits for
      // the lock → 10-20 s timeout → "Reservation Server Unreachable".
      //
      // Fix: await fetchProperties() first, then fire refreshTenantSchema() async
      // so its Liquibase lock cannot block the property load.
      const data = await fetchProperties();
      const list = Array.isArray(data) ? data : (data?.content ?? []);
      if (list.length > 0) {
        setProperty(list[0]);
        setPropertyId(list[0].id);
        registerAutoCacheProperty(list[0]);
        setOfflineBoot(false);
        // Kick off snapshot schedules immediately now that propertyId is known.
        // setPropertyId already calls pms.setAuth + requestSync, but call again
        // here as a belt-and-suspenders so the bootstrap cycle always starts
        // even if the first setPropertyId call raced with the IPC seed.
        window.pms?.requestSync?.().catch(() => {});
      } else if (!bootedFromCache) {
        setProperty(null);
      }
      fetchedRef.current = true;
      setError(null);
      setSetupWarning(null);

      // Only fire refreshTenantSchema AFTER the property fetch succeeds.
      // Running it concurrently caused the synchronized ensureSchema lock
      // to block fetchProperties and trigger the 10-20 s timeout.
      refreshTenantSchema().catch(() => {});
    } catch (e) {
      if (!bootedFromCache) {
        // FIX: for a first-time TENANT_ADMIN who has no property configured yet,
        // show PropertySetup instead of the "Server Unreachable" error screen.
        // The error screen was a dead-end: they couldn't set up their property
        // because the server appeared unreachable before any property existed.
        //
        // We detect "first-time setup" by checking: no cached property + TENANT_ADMIN role.
        // The server might just be slow (cold start / Liquibase running) — the user can
        // fill in the form and submit once the server recovers, or retry connection.
        const user   = typeof getCurrentUser === 'function' ? getCurrentUser() : null;
        const isAdmin = user?.role === 'TENANT_ADMIN' || user?.role === 'ADMIN';
        if (isAdmin) {
          // Show PropertySetup with a soft warning banner instead of blocking error.
          setProperty(null);
          setError(null);
          setSetupWarning(e.message);
        } else {
          setError(e.message);
        }
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  const loadModules = useCallback(async () => {
    try {
      const data = await fetchTenantModules();
      setModules(Array.isArray(data) ? data : []);
    } catch (_) {}
  }, []);

  // ── Kick off data loading once authenticated ──────────────────────────
  const initAfterAuth = useCallback((slug) => {
    fetchedRef.current  = false;
    fetchingRef.current = false;
    checkTenant(slug || getTenantSlug());
    loadProperty();
    loadModules();
  }, [checkTenant, loadProperty, loadModules]);

  // ── Bootstrap effect: runs after property loads on desktop ──────────────
  // Shows a "Loading Hotel Data" progress screen on first boot so the user
  // knows SQLite is being populated from the server.
  //
  // Triggers when:
  //  - Desktop app AND authenticated AND property resolved
  //  - SQLite has no lastSync time (never bootstrapped) OR bootstrapVersion < 3
  //
  // The SyncWorker emits bootstrapProgress (0-100) and phase transitions
  // ('bootstrap_start' … 'bootstrap_complete') on IPC.SYNC_STATUS so we can
  // show a real progress bar instead of an indeterminate spinner.
  useEffect(() => {
    if (!authed || !property?.id || !window.pms) return;

    let unsub;
    let timer;
    let done = false;

    const markDone = () => {
      if (done) return;
      done = true;
      setBootstrapNeeded(false);
      setBootstrapProgress(100);
      unsub?.();
      clearTimeout(timer);
    };

    window.pms.getSyncStatus().then(r => {
      const s = r?.data ?? r ?? {};

      // Show bootstrap screen when:
      //  a) No lastSync at all (first boot, SQLite is empty)
      //  b) bootstrapVersion is missing (never ran bootstrap v3)
      // Don't show it if the SyncWorker says it already completed this session.
      const needsBootstrap = !s.lastSync || !s.bootstrapVersion || Number(s.bootstrapVersion) < 3;

      if (!needsBootstrap) {
        markDone();
        return;
      }

      // Show the progress modal immediately — don't wait for the first status push
      setBootstrapNeeded(true);
      setBootstrapProgress(5);

      unsub = window.pms.onSyncStatus?.((status) => {
        // Update progress bar as SyncWorker reports entity-by-entity progress
        if (typeof status.bootstrapProgress === 'number' && status.bootstrapProgress > 0) {
          setBootstrapProgress(Math.max(5, status.bootstrapProgress));
        }
        const p = status.phase ?? '';
        // Done when bootstrap completes OR a normal idle/done phase is reached
        // (which means bootstrap already ran earlier this session)
        if (p === 'bootstrap_complete' || p === 'idle' || p === 'done') {
          markDone();
        }
      });

      // 45-second hard fallback — never permanently block the user
      // (45s gives enough time for large datasets on slow connections)
      timer = setTimeout(markDone, 45_000);
    }).catch(() => markDone()); // IPC failed — never block the UI

    return () => { unsub?.(); clearTimeout(timer); };
  }, [authed, property?.id]); // eslint-disable-line

  useEffect(() => {
    if (!authed || awaitingParentAuth.current) return;
    initAfterAuth();
  }, [authed]); // eslint-disable-line

  // ── Re-check property after bridge hydration ──────────────────────────
  // The offline data bridge hydrates IndexedDB from Dexie asynchronously at
  // boot.  If the property was not yet in IndexedDB when loadProperty() ran,
  // property stays null and all views remain blank.  Retry once the bridge
  // signals it has finished populating the cache.
  useEffect(() => {
    const tryRetryProperty = () => {
      if (!property && authed && !awaitingParentAuth.current) {
        fetchedRef.current  = false;
        fetchingRef.current = false;
        loadProperty();
      }
    };
    const onBridgeHydrated = () => tryRetryProperty();
    window.addEventListener('hotelmis:bridge:hydrated', onBridgeHydrated);
    if (window._hotelmisOfflineBridgeReady) {
      tryRetryProperty();
    }
    return () => window.removeEventListener('hotelmis:bridge:hydrated', onBridgeHydrated);
  }, [property, authed, loadProperty]); // eslint-disable-line

  // ── postMessage from Main Hall ────────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (!e.data || typeof e.data !== 'object') return;

      const { type, token, tenantSlug: slug, businessId } = e.data;

      if (type === 'HOTELMIS_AUTH') {
        if (!token) return;
        receiveAuthFromParent({ token, tenantSlug: slug, businessId });
        awaitingParentAuth.current = false;
        setAuthed(true);
        initAfterAuth(slug);
        return;
      }

      if (type === 'HOTELMIS_INIT' || type === 'HOTELMIS_TOKEN') {
        if (token) {
          receiveAuthFromParent({ token, tenantSlug: slug, businessId });
          awaitingParentAuth.current = false;
        }
        setAuthed(true);
        initAfterAuth(slug);
        return;
      }

      if (type === 'HOTELMIS_NAVIGATE') navigate(e.data.view, e.data.id);
    };

    window.addEventListener('message', h);

    const slug = getTenantSlug();
    window.parent?.postMessage({ type: 'HOTELMIS_READY',        moduleId: 'reservation', tenantSlug: slug }, '*');
    window.parent?.postMessage({ type: 'HOTELMIS_MODULE_READY', moduleId: 'reservation', tenantSlug: slug }, '*');

    return () => window.removeEventListener('message', h);
  }, []); // eslint-disable-line

  // ── Silent token refresh — proactive long-lived session management ────
  //
  // Three-layer strategy so hotel staff are NEVER logged out mid-shift:
  //
  //  1. Regular cycle: refresh every 15 min so the 7-day token is always
  //     reissued long before it can expire (was 20 min — tightened so a
  //     brief network blip has more recovery windows per hour).
  //
  //  2. Expiry watchdog: separate 5-min interval that checks if the stored
  //     token will expire within 2 hours.  Fires an immediate refresh when
  //     true — covers the case where the server was unreachable for hours and
  //     the regular cycle missed several beats.
  //
  //  3. Online-event hook: refresh immediately whenever connectivity is
  //     restored after an offline period.
  useEffect(() => {
    if (!property) return;

    // Layer 1: regular 15-min refresh
    const runRefresh = () => {
      window.addEventListener('hotelmis:backend:online', silentRefreshToken, { once: true });
      silentRefreshToken();
    };
    runRefresh();
    const refreshTimer = setInterval(runRefresh, 15 * 60 * 1000);

    // Layer 2: expiry watchdog every 5 min
    const watchdogTimer = setInterval(() => {
      if (isTokenExpiringSoon(2 * 60 * 60 * 1000)) {
        console.info('[Auth] Token expiring soon — triggering proactive refresh');
        silentRefreshToken();
      }
    }, 5 * 60 * 1000);

    // Layer 3: refresh on reconnect
    window.addEventListener('hotelmis:backend:online', silentRefreshToken);

    return () => {
      clearInterval(refreshTimer);
      clearInterval(watchdogTimer);
      window.removeEventListener('hotelmis:backend:online', silentRefreshToken);
    };
  }, [property?.id]); // eslint-disable-line

  // Register property with auto-cache whenever it changes
  useEffect(() => {
    if (property?.id) registerAutoCacheProperty(property);
  }, [property?.id]);

  // Clear offline-boot banner once the server is reachable again
  useEffect(() => {
    if (!offlineBoot) return;
    const onReconnect = () => {
      fetchProperties()
        .then(data => {
          const list = Array.isArray(data) ? data : (data?.content ?? []);
          if (list.length > 0) {
            setProperty(list[0]);
            setPropertyId(list[0].id);
            registerAutoCacheProperty(list[0]);
            setOfflineBoot(false);
            setError(null);
          }
        })
        .catch(() => {});
    };
    window.addEventListener('hotelmis:backend:online', onReconnect);
    return () => window.removeEventListener('hotelmis:backend:online', onReconnect);
  }, [offlineBoot]);

  // FIX (2026-06-04): when desktopBridge fires hotelmis:offline:resolved,
  // the user may be on a detail page whose selectedId is still the offline
  // temp-id.  Swap it for the real UUID so the component re-fetches correctly.
  // Without this, the component calls desktopGet('/reservations/offline-...')
  // which returns null after sync has tombstoned the offline record.
  useEffect(() => {
    const handler = (e) => {
      const { offlineId, realId } = e.detail ?? {};
      if (!offlineId || !realId) return;
      // If the user is currently viewing a detail page for the reconciled id,
      // swap selectedId to the real UUID so the component reloads correctly.
      setSelectedId(prev => (prev === offlineId ? realId : prev));
    };
    window.addEventListener('hotelmis:offline:resolved', handler);
    return () => window.removeEventListener('hotelmis:offline:resolved', handler);
  }, []);

  const navigate = (v, id = null) => {
    setView(v);
    setSelId(id);
    warmCacheForView(v, id);
  };

  // Warm the initial view once property is available
  useEffect(() => {
    if (property?.id) warmCacheForView(view, selectedId);
  }, [property?.id]); // eslint-disable-line

  const handleLogout = () => {
    clearAuth();
    fetchedRef.current = fetchingRef.current = false;
    awaitingParentAuth.current = window !== window.parent;
    setProperty(null); setAuthed(false); setView('dashboard'); setTenantBlock(null); setSetupWarning(null);
    window.parent?.postMessage({ type: 'HOTELMIS_LOGOUT' }, '*');
  };

  // ── Tenant not subscribed — non-dismissible half-page modal ─────────────
  if (tenantBlock) return (
    <div style={{
      position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(135deg,${T.navyDeep},${T.navyMid})`,
      fontFamily: FONTS.body, padding: 20,
      /* No onClick — non-dismissible */
    }}>
      <div style={{
        background: T.white, borderRadius: 20,
        padding: '52px 44px',
        width: 'min(50vw, 600px)', minWidth: 320,
        textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,.35)',
      }}>
        {/* Lock icon */}
        <div style={{ fontSize: 52, marginBottom: 20, lineHeight: 1 }}>🔒</div>

        <h2 style={{ fontFamily: FONTS.display, fontSize: 24, color: T.navy, marginBottom: 8 }}>
          Module Not Activated
        </h2>

        <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.6, marginBottom: 4 }}>
          The <strong>Reservation Module</strong> is not active for tenant{' '}
          <code style={{ background: T.surface, padding: '2px 6px', borderRadius: 4 }}>{tenantSlug}</code>.
        </p>

        <p style={{ fontSize: 12, color: T.muted, marginBottom: 24 }}>{tenantBlock.reason}</p>

        {/* Admin instruction */}
        <div style={{
          background: '#f0fdfa', border: '1.5px solid #99f6e4',
          borderRadius: 12, padding: '18px 20px',
        }}>
          <p style={{ fontSize: 14, color: T.navy, lineHeight: 1.75, margin: 0 }}>
            Please ask your <strong>administrator</strong> to activate this module
            from the <strong style={{ color: T.teal }}>Control Centre</strong>.
          </p>
        </div>
      </div>
    </div>
  );

  if (!authed) return (
    <StandaloneLogin onLogin={(loginData) => {
      // loginData arrives from persistStaffAuth call inside StandaloneLogin —
      // it contains { token, tenantSlug, propertyId, ...serverFields }.
      // We must call initAfterAuth here so loadProperty() fires immediately
      // after the user logs in, rather than waiting for the next render cycle.
      // Also ensure propertyId is written to localStorage NOW so getCachedProperties()
      // and every subsequent queryTable call can filter correctly.
      if (loginData?.propertyId) {
        setPropertyId(loginData.propertyId);
      }
      setAuthed(true);
      // initAfterAuth resets fetchedRef/fetchingRef and kicks off loadProperty
      // + loadModules in one go — this is the call that was missing before.
      initAfterAuth(loginData?.tenantSlug);
    }} />
  );

  // Show bootstrap progress screen after login while SQLite is being populated
  if (bootstrapNeeded && window.pms) {
    return <BootstrapLoadingScreen progress={bootstrapProgress} />;
  }

  if (awaitingParentAuth.current) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: '100vh', gap: 16, fontFamily: FONTS.body }}>
      <Spinner size={40} />
      <p style={{ fontSize: 13, color: T.muted }}>Connecting to Main Hall…</p>
    </div>
  );

  const renderContent = () => {
    if (loading) return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '70vh', gap: 16, fontFamily: FONTS.body }}>
        <Spinner size={40} />
        <p style={{ fontSize: 13, color: T.muted }}>Connecting to reservation module…</p>
        <p style={{ fontSize: 10, fontFamily: 'monospace', color: '#9EACBE' }}>tenant: {tenantSlug}</p>
      </div>
    );

    if (error) return (
      <div style={{ maxWidth: 520, margin: '60px auto', padding: '0 20px' }}>
        <div style={{ background: T.dangerPale, border: `1px solid ${T.danger}30`, borderRadius: 14,
          padding: 32, textAlign: 'center' }}>
          <p style={{ fontSize: 36, marginBottom: 12 }}>📵</p>
          <p style={{ fontSize: 16, fontWeight: 700, color: T.danger, marginBottom: 8 }}>
            Reservation Server Unreachable
          </p>
          <p style={{ fontSize: 13, color: T.slate, lineHeight: 1.6, marginBottom: 8 }}>
            {error}
          </p>
          <p style={{ fontSize: 12, color: T.muted, marginBottom: 20, background: `${T.gold}18`,
            borderRadius: 8, padding: '10px 16px', lineHeight: 1.5 }}>
            Your recent work may still be available locally. Retry when the server is back online.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button onClick={() => { fetchedRef.current = false; setError(null); setSetupWarning(null); loadProperty(); }}
              style={{ background: T.teal, border: 'none', borderRadius: 9, padding: '10px 22px',
                fontSize: 13, fontWeight: 700, color: T.white, cursor: 'pointer', fontFamily: FONTS.body }}>
              🔄 Retry Connection
            </button>
            {error.includes('401') && (
              <button onClick={handleLogout}
                style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 9,
                  padding: '10px 22px', fontSize: 13, color: T.slate, cursor: 'pointer', fontFamily: FONTS.body }}>
                Login Again
              </button>
            )}
          </div>
        </div>
      </div>
    );

    if (!property) return (
      <>
        {/* setupWarning: server was unreachable on first load but we show PropertySetup
            anyway for a first-time TENANT_ADMIN. They can fill the form and save once
            the server is reachable, or use the retry button to reload. */}
        {setupWarning && (
          <div style={{ maxWidth: 700, margin: '16px auto 0', padding: '0 20px' }}>
            <div style={{ background: '#FFF8E1', border: '1px solid #F9A825', borderRadius: 10,
              padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#7A5400', marginBottom: 3 }}>
                  Server unreachable — first-time setup mode
                </p>
                <p style={{ fontSize: 12, color: '#856404', lineHeight: 1.5, margin: 0 }}>
                  Could not reach the reservation server ({setupWarning}).
                  You can fill in your property details below, but saving will require the server to be online.
                </p>
              </div>
              <button onClick={() => { fetchedRef.current = false; setSetupWarning(null); loadProperty(); }}
                style={{ background: '#F9A825', border: 'none', borderRadius: 7, padding: '6px 14px',
                  fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', flexShrink: 0 }}>
                🔄 Retry
              </button>
            </div>
          </div>
        )}
        <PropertySetup property={null} onPropertyCreated={p => { setProperty(p); setPropertyId(p.id); setSetupWarning(null); }} />
      </>
    );

    switch (view) {
      case 'dashboard':          return <Dashboard         property={property} onNavigate={navigate} />;
      case 'frontdesk':          return <FrontDesk         property={property} onNavigate={navigate} />;
      case 'reservations':       return <ReservationList   property={property} onSelect={id => navigate('reservation-detail', id)} onNew={() => navigate('new-reservation')} />;
      case 'new-reservation':    return <CreateReservation property={property} onCreated={(r, wasOffline) => { if (wasOffline) navigate('reservations'); else navigate('reservation-detail', r.id); }} onCancel={() => navigate('reservations')} />;
      case 'reservation-detail': return <ReservationDetail reservationId={selectedId} property={property} onBack={() => navigate('reservations')} onNavigate={navigate} />;
      case 'rooms':              return <RoomStatusBoard   property={property} />;
      case 'rates':              return <RatePlans         property={property} />;
      case 'folio':              return selectedId
        ? <FolioSheet reservationId={selectedId} property={property} onBack={() => navigate('reservations')} />
        : <ReservationList property={property} onSelect={id => navigate('folio', id)} onNew={() => navigate('new-reservation')} />;
      case 'housekeeping':       return <HousekeepingBoard property={property} />;
      case 'guests':             return <GuestManagement   property={property} onNavigate={navigate} />;
      case 'corporate-accounts': return <CorporateAccounts property={property} />;
      case 'corporate-sessions': return <CorporateSessions property={property} />;
      case 'staff':              return <StaffManagement   property={property} onNavigate={navigate} />;
      case 'staff-detail':       return <StaffDetail       staffId={selectedId} property={property} onBack={() => navigate('staff')} onNavigate={navigate} />;
      case 'staff-logs':         return <StaffDetail       staffId={selectedId} property={property} onBack={() => navigate('staff')} onNavigate={navigate} initialTab="logs" />;
      case 'audit-logs':         return <AuditLogs         property={property} onNavigate={navigate} />;
      case 'booking':            return <OnlineBooking     property={property} />;
      case 'financial':          return <FinancialStatement property={property} />;
      case 'reports':            return <Reports           property={property} />;
      case 'district-report':    return <DistrictReport    property={property} />;
      case 'settings':           return <PropertySetup     property={property} onPropertyCreated={p => { setProperty(p); setPropertyId(p.id); setSetupWarning(null); }} onPropertyUpdated={p => setProperty(p)} />;
      case 'email-config':       return <EmailConfig       property={property} />;
      case 'profile':            return <ProfilePage       onLogout={handleLogout} />;
      default:                   return <Dashboard         property={property} onNavigate={navigate} />;
    }
  };

  return (
    <ErrorBoundary>
      <FrontDeskLayout currentView={view} property={property} tenantSlug={tenantSlug}
        modules={modules} onNavigate={navigate} onLogout={handleLogout} offlineBoot={offlineBoot}>
        <ErrorBoundary>
          {renderContent()}
        </ErrorBoundary>
      </FrontDeskLayout>
    </ErrorBoundary>
  );
}